import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import useIraApplicationFundingData from '../../hooks/useApplicationIraFundingData';
import { selectCountries } from '../../store/selectors/commonSelectors';
import { selectPortalGatewayISOCodesListToIgnoreAreaCode } from '../../store/selectors/settingsSelectors';
import Back from '../Buttons/Back';
import { ModalHeading } from '../Modal';
import Paragraph from '../Paragraph/Paragraph';
import IraFundingReview from '../Review/IraFundingReview';
import ReviewData from '../Review/ReviewData';
import AddressReviewFormat from '../AddressReviewFormat';
import { formatPhoneNumberForView } from '../../util/helpers/phoneNumberHelpers';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import { separateByUppercase } from '../../util/helpers/stringHelpers';
import { parseEnumType } from '../../util/helpers/enumMappers';
import { IRA_TRANSFER_TYPES } from '../../util/constants';
import themeColors from '../../assets/styles/themeColors';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  padding: 0 1.5rem 0 1.5rem;

  @media only screen and (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const CustodianReview = ({ handleBack, goToStepsBack, onModalClose }) => {
  const { t } = useTranslation();
  const { IraApplicationFundingResponse } = useIraApplicationFundingData();
  const {
    IRAFundingCustodianResponse,
    TransferType,
  } = IraApplicationFundingResponse;
  const {
    Name,
    AccountNumber,
    Address,
    Phone,
    Fax,
  } = IRAFundingCustodianResponse;
  const selectedISOCodesListToIgnoreAreaCode = useSelector(
    selectPortalGatewayISOCodesListToIgnoreAreaCode,
  );
  const countries = useSelector(selectCountries);

  return (
    <>
      {handleBack && (
        <Back
          onClick={() => {
            handleBack();
          }}
        />
      )}
      <ModalHeading>{t('iraCustodian.review.title')}</ModalHeading>
      <Paragraph mb={24} color={themeColors.colorTextXLLight}>
        {t('iraCustodian.review.description')}
      </Paragraph>
      {!!IraApplicationFundingResponse?.IRAFundingType && (
        <IraFundingReview
          mb={24}
          onEdit={() => {
            goToStepsBack(1);
          }}
          funding={IraApplicationFundingResponse}
        />
      )}
      <Grid>
        <ReviewData
          title={t('iraCustodian.review.labels.custodianName')}
          onEdit={() => {
            goToStepsBack(5);
          }}
        >
          {Name}
        </ReviewData>
        {Address?.State && (
          <ReviewData
            title={t('iraCustodian.review.labels.custodianAddress')}
            onEdit={() => {
              goToStepsBack(5);
            }}
          >
            <AddressReviewFormat {...Address} isCustodian />
          </ReviewData>
        )}
        {Phone.Iso3CountryCode && (
          <ReviewData
            title={t('iraCustodian.review.labels.custodianPhoneNumber')}
            onEdit={() => {
              goToStepsBack(5);
            }}
          >
            {formatPhoneNumberForView(
              Phone,
              countries,
              selectedISOCodesListToIgnoreAreaCode,
            )}
          </ReviewData>
        )}
        {Fax.Iso3CountryCode && (
          <ReviewData
            title={t('iraCustodian.review.labels.custodianFaxNumber')}
            onEdit={() => {
              goToStepsBack(5);
            }}
          >
            {formatPhoneNumberForView(
              Fax,
              countries,
              selectedISOCodesListToIgnoreAreaCode,
            )}
          </ReviewData>
        )}
        <ReviewData
          title={t('iraCustodian.review.labels.transferType')}
          onEdit={() => {
            goToStepsBack(3);
          }}
        >
          {separateByUppercase(parseEnumType(IRA_TRANSFER_TYPES, TransferType))}
        </ReviewData>
        <ReviewData
          title={t('iraCustodian.review.labels.accountNumber')}
          onEdit={() => {
            goToStepsBack(4);
          }}
        >
          {AccountNumber}
        </ReviewData>
      </Grid>

      <OnBoardingCta onClick={onModalClose} />
    </>
  );
};

CustodianReview.propTypes = {
  onModalClose: PropTypes.func,
  handleBack: PropTypes.func,
  handleNext: PropTypes.func,
  goToStepsBack: PropTypes.func,
};

export default CustodianReview;
